import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, Table } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import moment from "moment";

import { useLeadFormActions } from "./hooks/useLeadFormActions";
import { PAGE_LENGTH } from "../../config";
import { configType } from "../../constant/WebToLead";
import ActionMenu from "./ActionMenu";
import {
  emptyGeneratedHtml,
  generateHtml,
} from "../../actionCreator/webToLeadActionCreater";
import Status from "./Status";
import htmlIcon from "../../assets/images/webToLead/html.svg";
import linkIcon from "../../assets/images/webToLead/link.svg";
import { getSubscriptionUserInfo } from "../../utils/utils";
import Preview from "./Preview";
import Share from "./Share";

const FormsList = ({ onSubscription }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { leadForms, loading, getLeadFormsList } = useLeadFormActions();
  const [page, setPage] = useState(1);
  const [showPreview, setShowPreview] = useState(null);
  const generatedHtml = useSelector(
    (state) => state.WebToLeadReducer.generatedHtml
  );
  const previewLoading = useSelector((state) => state.WebToLeadReducer.loading);
  const subscription = getSubscriptionUserInfo();
  const currentDate = moment();
  const validToDate = moment(subscription?.validTo);
  const isExpired = validToDate.isBefore(currentDate);
  const dayDiff = currentDate.diff(validToDate, "days");

  const onPageChange = (page, pageSize) => {
    setPage(page);
  };

  const handleCancel = () => {
    setShowPreview(null);
    dispatch(emptyGeneratedHtml());
  };

  useEffect(() => {
    getLeadFormsList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const columns = [
    {
      title: <></>,
      dataIndex: "active",
      width: 70,
      render: (record, row) => {
        return (
          <div className="table-forms">
            {row.configType === configType.EMBEDDED_HTML && (
              <div
                className={`preview ${showPreview === row?.id && "preview-active"}`}
                onClick={() => {
                  dispatch(
                    generateHtml(
                      row?.id,
                      row.configType === configType.EMBEDDED_SCRIPT
                    )
                  );
                  setShowPreview(row?.id);
                }}
              >
                <EyeOutlined />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <span className="heading13px roboto-medium text-color-1C1C1C">
          Forms
        </span>
      ),
      dataIndex: "formName",
      render: (record, row) => {
        return (
          <div className="d-flex align-items-center">
            <div
              className="text-theme roboto-medium heading13px cursor-pointer"
              onClick={() => {
                if (isExpired && dayDiff > 7) {
                  onSubscription();
                } else {
                  navigate(`/settings/web-to-lead/${row.id}`);
                }
              }}
            >
              {record}
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <span className="heading13px roboto-medium text-color-1C1C1C">
          Form type
        </span>
      ),
      dataIndex: "configType",
      render: (record, row) => {
        return (
          <>
            {record !== configType.SHARED_URL ? (
              <div className="d-flex align-items-center" style={{ gap: 5 }}>
                <img src={htmlIcon} alt="" width={16} height={16} />{" "}
                <span className="html-badge">
                  {record === configType.EMBEDDED_SCRIPT ? "Script" : "HTML"}
                </span>
                <div className="mx-2">
                  <Share formData={row} isCopy />
                </div>
              </div>
            ) : (
              <div className="d-flex align-items-center" style={{ gap: 5 }}>
                <img src={linkIcon} alt="" width={16} height={16} />
                <span className="link-badge">Link</span>
              </div>
            )}
          </>
        );
      },
    },
    {
      title: (
        <span className="heading13px roboto-medium text-color-1C1C1C">
          Created Date
        </span>
      ),
      dataIndex: "createdDate",
      render: (record, row) => {
        return (
          <div className="roboto-medium heading13px text-color-1F1F1F">
            {moment(record).format("MMM DD, YYYY")}
          </div>
        );
      },
    },
    {
      title: (
        <span className="heading13px roboto-medium text-color-1C1C1C">
          Status
        </span>
      ),
      dataIndex: "active",
      render: (record, row) => {
        return <Status record={record} row={row} page={page} />;
      },
    },
    {
      title: <></>,
      dataIndex: "active",
      render: (record, row) => {
        return (
          <ActionMenu
            row={row}
            page={page}
            setPage={setPage}
            isExpired={isExpired && dayDiff > 7}
            onSubscription={onSubscription}
          />
        );
      },
    },
  ];

  const tableProps =
    isExpired && dayDiff > 7
      ? {
          locale: {
            emptyText: (
              <div className="text-error" style={{ padding: "20px 0" }}>
                Your subscription has expired. Please renew your subscription to
                continue using the service.
              </div>
            ),
          },
        }
      : {};

  return (
    <div className="mt20">
      <Table
        style={{ border: "1px solid #e1e1e1", borderRadius: "4px" }}
        columns={columns}
        loading={loading}
        dataSource={leadForms?.content}
        size="middle"
        pagination={{
          responsive: true,
          position: ["bottomCenter"],
          pageSize: PAGE_LENGTH,
          total: leadForms?.totalElements,
          defaultCurrent: page,
          current: page,
          onChange: onPageChange,
        }}
        {...tableProps}
      />
      <div className="text-color-030334 heading12px mt10">
        *Note: Deactivating a form will disable its link to process, making it
        inaccessible.
      </div>
      <Modal
        title={<></>}
        open={!!showPreview}
        centered
        className="form-preview"
        width={650}
        footer={null}
        loading={previewLoading}
        onOk={handleCancel}
        onCancel={handleCancel}
      >
        <div className="overflow-y-scroll">
          <Preview generatedHtml={generatedHtml} />
        </div>
      </Modal>
    </div>
  );
};

export default FormsList;
